// The jobs filter popover, ported from ionq-cloud-ui's FilterButton:
// same fields, order, labels and staged-until-Apply behavior. The staged
// state is a plain draft + per-row enabled flags (the remote used
// react-hook-form plus checkbox mirrors for the same job), and the selects
// are Chakra v3 natives instead of chakra-react-select — selection semantics
// (multi for status/backend, single for project and the date operator) are
// unchanged. Rows left enabled with an empty value simply serialize to
// nothing, exactly like the remote.

import {
    Box,
    Button,
    Checkbox,
    createListCollection,
    Flex,
    Icon,
    IconButton,
    Input,
    Popover,
    Portal,
    Select,
    Spacer,
    useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import { Tooltip } from '../../components/Tooltip';
import {
    BACKEND_FILTER_OPTIONS,
    DATE_RANGE_SELECTOR_OPTIONS,
    type DateRangeSelector,
    hasActiveFilters,
    JOB_STATUSES,
    type SelectOption,
    type UiFilters,
} from '../../lib/jobs/filters';
import { getFriendlyTargetName } from '../../lib/jobs/targets';

const STATUS_OPTIONS: SelectOption[] = JOB_STATUSES.map((status) => ({
    value: status,
    label: status,
}));

const BACKEND_OPTIONS: SelectOption[] = BACKEND_FILTER_OPTIONS.map(
    (backend) => ({ value: backend, label: getFriendlyTargetName(backend) }),
);

const DATE_OPTIONS: SelectOption[] = DATE_RANGE_SELECTOR_OPTIONS.map(
    (selector) => ({ value: selector, label: selector }),
);

const FILTER_ROWS = [
    'date',
    'status',
    'name',
    'backend',
    'id',
    'project',
    'metadata',
] as const;
type FilterRowKey = (typeof FILTER_ROWS)[number];

const enabledRowsFor = (filters: UiFilters): Record<FilterRowKey, boolean> => ({
    date: !!filters.dateRangeSelector,
    status: !!filters.status?.length,
    name: !!filters.name,
    backend: !!filters.backend?.length,
    id: !!filters.id,
    project: !!filters.projectId,
    metadata: !!filters.metadataKey,
});

/** Draft → applied filters: rows toggled off contribute nothing. */
const composeFilters = (
    draft: UiFilters,
    enabled: Record<FilterRowKey, boolean>,
): UiFilters => ({
    ...(enabled.date && {
        dateRangeSelector: draft.dateRangeSelector,
        filterDate: draft.filterDate,
        filterEndDate: draft.filterEndDate,
    }),
    ...(enabled.status && { status: draft.status }),
    ...(enabled.name && { name: draft.name?.trim() }),
    ...(enabled.backend && { backend: draft.backend }),
    ...(enabled.id && { id: draft.id?.trim() }),
    ...(enabled.project && { projectId: draft.projectId }),
    // A value without a key applies no predicate server-side — emitting it
    // would mark the URL "filtering" over an unfiltered result.
    ...(enabled.metadata &&
        draft.metadataKey?.trim() && {
            metadataKey: draft.metadataKey.trim(),
            metadataValue: draft.metadataValue,
        }),
});

function FilterRow({
    name,
    active,
    disabled,
    onToggle,
    children,
}: {
    name: string;
    active: boolean;
    disabled?: boolean;
    onToggle: (checked: boolean) => void;
    children?: React.ReactNode;
}) {
    return (
        <Box
            padding='8px 16px'
            border='1px solid'
            borderColor='border'
            borderTop='0px'
        >
            <Checkbox.Root
                name={`${name}Checkbox`}
                size='sm'
                fontWeight='500'
                color='fg.muted'
                checked={active}
                onCheckedChange={({ checked }) => onToggle(checked === true)}
                disabled={disabled}
            >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>{name}</Checkbox.Label>
            </Checkbox.Root>
            {active ? <Box mt='8px'>{children}</Box> : null}
        </Box>
    );
}

function FilterSelect({
    options,
    placeholder,
    multiple = false,
    value,
    onChange,
}: {
    options: SelectOption[];
    placeholder: string;
    multiple?: boolean;
    value: string[];
    onChange: (value: string[]) => void;
}) {
    const collection = useMemo(
        () =>
            createListCollection({
                items: options,
                itemToString: (item) => item.label,
                itemToValue: (item) => item.value,
            }),
        [options],
    );
    return (
        <Select.Root
            size='sm'
            multiple={multiple}
            closeOnSelect={!multiple}
            collection={collection}
            value={value}
            onValueChange={({ value: next }) => onChange(next)}
        >
            <Select.HiddenSelect />
            <Select.Control>
                <Select.Trigger>
                    <Select.ValueText placeholder={placeholder} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
                <Select.Positioner>
                    <Select.Content zIndex='popover'>
                        {collection.items.map((item) => (
                            <Select.Item item={item} key={item.value}>
                                {item.label}
                                <Select.ItemIndicator />
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    );
}

export const FilterButton = ({
    filters,
    setFilters,
    projectOptions,
}: {
    filters: UiFilters;
    setFilters: (newFilters: UiFilters) => void;
    /** Project select choices, from the projects query. */
    projectOptions: SelectOption[];
}) => {
    const { onOpen, onClose, open: isOpen } = useDisclosure();
    const isFiltering = hasActiveFilters(filters);
    // Staged while the popover is open; re-seeded from the applied filters
    // on every open.
    const [draft, setDraft] = useState<UiFilters>(filters);
    const [enabled, setEnabled] = useState(() => enabledRowsFor(filters));

    const patch = (fields: Partial<UiFilters>) =>
        setDraft((current) => ({ ...current, ...fields }));

    const toggleRow = (row: FilterRowKey, checked: boolean) => {
        setEnabled((current) => ({ ...current, [row]: checked }));
        // Donor parity: enabling the Date row leaves the operator select on
        // its placeholder — no filter applies until the user picks one.
        // Pre-seeding 'equals' silently narrowed to a single day.
        if (row === 'date' && !checked) {
            patch({
                dateRangeSelector: '',
                filterDate: '',
                filterEndDate: '',
            });
        }
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setFilters(composeFilters(draft, enabled));
        onClose();
    };

    return (
        <Popover.Root
            positioning={{ placement: 'bottom-start' }}
            open={isOpen}
            onOpenChange={({ open }) => {
                if (open) {
                    setDraft(filters);
                    setEnabled(enabledRowsFor(filters));
                    onOpen();
                } else {
                    onClose();
                }
            }}
        >
            <Popover.Trigger asChild>
                <IconButton
                    size='sm'
                    variant='ghost'
                    aria-label={isFiltering ? 'Filtering' : 'Filter'}
                >
                    {/* Tooltip on the icon, not the button: wrapping the
                        popover trigger in a tooltip trigger merges two zag
                        anchors onto one element and the popover loses its
                        positioning reference. */}
                    <Tooltip content={isFiltering ? 'Filtering' : 'Filter'}>
                        <Icon
                            as={MdFilterList}
                            width='16px'
                            height='16px'
                            color={isFiltering ? 'fg.link' : 'fg.muted'}
                        />
                    </Tooltip>
                </IconButton>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content width='300px'>
                        <Popover.Body m={0} p={0}>
                            <form onSubmit={submit}>
                                <Box borderRadius='5px' bg='bg.panel'>
                                    <Flex
                                        borderRadius='5px'
                                        borderBottomEndRadius={0}
                                        borderBottomStartRadius={0}
                                        padding='8px 16px 0'
                                        border='1px solid'
                                        borderColor='border'
                                        bg='bg.subtle'
                                        maxHeight='46px'
                                    >
                                        <Button
                                            size='sm'
                                            variant='outline'
                                            onClick={() => {
                                                setFilters({});
                                                onClose();
                                            }}
                                        >
                                            Clear
                                        </Button>
                                        <Spacer />
                                        <Button
                                            size='sm'
                                            variant='brand'
                                            mb={3}
                                            type='submit'
                                        >
                                            Apply
                                        </Button>
                                    </Flex>

                                    <FilterRow
                                        name='Date'
                                        active={enabled.date}
                                        onToggle={(checked) =>
                                            toggleRow('date', checked)
                                        }
                                    >
                                        <FilterSelect
                                            options={DATE_OPTIONS}
                                            placeholder='Choose setting'
                                            value={
                                                draft.dateRangeSelector
                                                    ? [draft.dateRangeSelector]
                                                    : []
                                            }
                                            onChange={([selector]) =>
                                                patch({
                                                    dateRangeSelector:
                                                        (selector as DateRangeSelector) ??
                                                        '',
                                                })
                                            }
                                        />
                                        <Input
                                            size='sm'
                                            mt='8px'
                                            type='date'
                                            value={draft.filterDate ?? ''}
                                            onChange={(e) =>
                                                patch({
                                                    filterDate: e.target.value,
                                                })
                                            }
                                        />
                                        {draft.dateRangeSelector ===
                                            'between' && (
                                            <Input
                                                size='sm'
                                                mt='8px'
                                                type='date'
                                                value={
                                                    draft.filterEndDate ?? ''
                                                }
                                                onChange={(e) =>
                                                    patch({
                                                        filterEndDate:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        )}
                                    </FilterRow>

                                    <FilterRow
                                        name='Status'
                                        active={enabled.status}
                                        onToggle={(checked) =>
                                            toggleRow('status', checked)
                                        }
                                    >
                                        <FilterSelect
                                            options={STATUS_OPTIONS}
                                            placeholder='Choose status'
                                            multiple
                                            value={(draft.status ?? []).map(
                                                (option) => option.value,
                                            )}
                                            onChange={(next) =>
                                                patch({
                                                    status: next.map(
                                                        (value) => ({
                                                            value,
                                                            label: value,
                                                        }),
                                                    ),
                                                })
                                            }
                                        />
                                    </FilterRow>

                                    <FilterRow
                                        name='Name'
                                        active={enabled.name}
                                        onToggle={(checked) =>
                                            toggleRow('name', checked)
                                        }
                                    >
                                        <Input
                                            size='sm'
                                            placeholder='contains'
                                            value={draft.name ?? ''}
                                            onChange={(e) =>
                                                patch({ name: e.target.value })
                                            }
                                        />
                                    </FilterRow>

                                    <FilterRow
                                        name='Backend'
                                        active={enabled.backend}
                                        onToggle={(checked) =>
                                            toggleRow('backend', checked)
                                        }
                                    >
                                        <FilterSelect
                                            options={BACKEND_OPTIONS}
                                            placeholder='Choose backend'
                                            multiple
                                            value={(draft.backend ?? []).map(
                                                (option) => option.value,
                                            )}
                                            onChange={(next) =>
                                                patch({
                                                    backend: next.map(
                                                        (value) => ({
                                                            value,
                                                            label: getFriendlyTargetName(
                                                                value,
                                                            ),
                                                        }),
                                                    ),
                                                })
                                            }
                                        />
                                    </FilterRow>

                                    <FilterRow
                                        name='ID'
                                        active={enabled.id}
                                        onToggle={(checked) =>
                                            toggleRow('id', checked)
                                        }
                                    >
                                        <Input
                                            size='sm'
                                            placeholder='enter full ID'
                                            value={draft.id ?? ''}
                                            onChange={(e) =>
                                                patch({ id: e.target.value })
                                            }
                                        />
                                    </FilterRow>

                                    <FilterRow
                                        name='Project'
                                        active={enabled.project}
                                        // An ACTIVE row must stay toggleable
                                        // even while the options are empty
                                        // (loading, or the project vanished)
                                        // — otherwise a URL-applied filter
                                        // can't be turned off.
                                        disabled={
                                            !projectOptions.length &&
                                            !enabled.project
                                        }
                                        onToggle={(checked) =>
                                            toggleRow('project', checked)
                                        }
                                    >
                                        <FilterSelect
                                            options={projectOptions}
                                            placeholder='Choose project'
                                            value={
                                                draft.projectId
                                                    ? [draft.projectId]
                                                    : []
                                            }
                                            onChange={([projectId]) =>
                                                patch({
                                                    projectId: projectId ?? '',
                                                })
                                            }
                                        />
                                    </FilterRow>

                                    <FilterRow
                                        name='Metadata'
                                        active={enabled.metadata}
                                        onToggle={(checked) =>
                                            toggleRow('metadata', checked)
                                        }
                                    >
                                        <Input
                                            size='sm'
                                            placeholder='key'
                                            value={draft.metadataKey ?? ''}
                                            onChange={(e) =>
                                                patch({
                                                    metadataKey: e.target.value,
                                                })
                                            }
                                        />
                                        <Input
                                            size='sm'
                                            mt='8px'
                                            placeholder='value'
                                            value={draft.metadataValue ?? ''}
                                            onChange={(e) =>
                                                patch({
                                                    metadataValue:
                                                        e.target.value,
                                                })
                                            }
                                        />
                                    </FilterRow>
                                </Box>
                            </form>
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};
